import React, { forwardRef } from 'react';
import { RegisterOptions, UseFormRegisterReturn } from 'react-hook-form';

import Description from '../description/Description';

interface DataType {
  id: string;
  value: any;
  label: string;
}

interface InputProps {
  name: string;
  data: DataType[];
  disabled?: boolean;
  readOnly?: boolean;
  align?: 'vertical' | 'horizontal';
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  description?: string | React.ReactNode;
}

interface InputWithCheckRadioHookFormProps extends InputProps {
  register?: (name: string, rules?: RegisterOptions) => UseFormRegisterReturn;
  errors?: any;
  rules?: RegisterOptions;
}

export default function withCheckRadioHookForm<
  T extends React.ComponentType<any>,
>(Component: T) {
  const WrappedComponent = forwardRef(
    (
      {
        name,
        data,
        register,
        errors,
        align = 'horizontal',
        rules,
        onChange,
        onBlur,
        description,
        disabled,
        readOnly,
        ...props
      }: InputProps & { register?: any; errors?: any; rules?: any },
      ref: React.ForwardedRef<any>,
    ) => {
      return (
        <>
          <div
            className={
              align === 'horizontal' ? 'flex flex-col' : 'flex flex-wrap'
            }
          >
            {data?.map((d, idx) => {
              return (
                <Component
                  key={idx}
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={disabled}
                  label={d.label}
                  value={d.value}
                  readOnly={readOnly}
                  ref={ref}
                  {...(register && register(name, rules))}
                  {...props}
                />
              );
            })}
          </div>
          <Description name={name} errors={errors} description={description} />
        </>
      );
    },
  ) as React.ComponentType<
    React.ComponentPropsWithoutRef<T> &
      Omit<InputWithCheckRadioHookFormProps, 'ref'>
  >;

  return WrappedComponent;
}
